import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Card, CardResponse } from "@yleisradio/areena-types";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { UI_SIZE } from "../../enums";
import { getYleIdsFromCards } from "../../helpers/areenaApi";
import { getRecommendationUrl } from "../../helpers/areenaLinks";
import { usePrevious } from "../../helpers/commonUtils";
import { fetchRecommendations } from "../../service/areena-recommendations";
import { usePlayerStore } from "../../store/usePlayerStore";
import { PlayerUpdateEvent } from "../../types/common";
import { EndRecommendations } from "../recommendations/EndRecommendations";
import { PauseRecommendations } from "../recommendations/PauseRecommendations";
import {
  CommonCardProps,
  getUiSize,
  SetPlayingRecommendation
} from "../recommendations/recommendationTypes";

const recommendationsShownEvent = (recommendations: Card[]) =>
  getYleIdsFromCards(recommendations).join(",");

interface Props {
  disabled: boolean;
  hasNextEpisode: boolean;
  hideRecommendationsOverlay?: () => void;
  newPlayerUi?: boolean;
  showRecommendationsOverlay?: () => void;
  uiRef?: HTMLDivElement | null;
  update: PlayerUpdateEvent;
}

const Recommendations: FC<Props> = ({
  disabled,
  hasNextEpisode,
  hideRecommendationsOverlay,
  newPlayerUi = false,
  showRecommendationsOverlay,
  uiRef,
  update
}) => {
  const { getState, subscribe, useStoreState } = usePlayerStore();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isEnded = useStoreState.isEnded();
  const isFullscreen = useStoreState.isFullscreen();
  const isPaused = useStoreState.isPaused();
  const isSeeking = useStoreState.isSeeking();
  const uiSize = useStoreState.uiSize();
  const programId = useStoreState.programId();
  const contentType = useStoreState.contentType();
  const uiSizeInPx = useStoreState.uiSizeInPx();

  const previousIsFullScreen = usePrevious(isFullscreen);
  const previousIsSeeking = usePrevious(isSeeking);

  const { language } = useEmbedParametersContext();

  const [cardResponse, setCardResponse] = useState<CardResponse | null>(null);
  const [recommendationsFetched, setRecommendationsFetched] =
    useState<boolean>(false);
  const [showEndRecommendations, setShowEndRecommendations] =
    useState<boolean>(false);
  const [showPauseRecommendations, setShowPauseRecommendations] =
    useState<boolean>(false);
  const [shouldFetchRecommendations, setShouldFetchRecommendations] =
    useState<boolean>(false);
  const [blockPauseRecommendations, setBlockPauseRecommendations] =
    useState<boolean>(false);
  const [recommendationsShown, setRecommendationsShown] =
    useState<boolean>(false);

  const playingRecommendationIndexRef = useRef<undefined | number>(undefined);
  const programIdRef = useRef(programId);
  const languageRef = useRef(language);

  useEffect(() => {
    programIdRef.current = programId;
  }, [programId]);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    if (isEnded) {
      setShowPauseRecommendations(false);
      if (!hasNextEpisode) {
        setShowEndRecommendations(true);
      }
    }
  }, [isEnded, hasNextEpisode]);

  useEffect(() => {
    if (
      uiSize === UI_SIZE.LARGE &&
      isPaused &&
      !isEnded &&
      !blockPauseRecommendations
    ) {
      setShouldFetchRecommendations(true);
      setShowPauseRecommendations(true);
    }
  }, [uiSize, isPaused, blockPauseRecommendations, isEnded]);

  useEffect(() => {
    if (!isPaused) {
      uiRef?.focus();
      setShowEndRecommendations(false);
      setShowPauseRecommendations(false);
    }
  }, [isPaused, uiRef]);

  useEffect(() => {
    if (uiSize !== UI_SIZE.LARGE) {
      setShowPauseRecommendations(false);
      setShowEndRecommendations(false);
    }
  }, [uiSize]);

  useEffect(() => {
    if (hasNextEpisode || !durationInSeconds || shouldFetchRecommendations) {
      return;
    }
    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        if (durationInSeconds - currentTime < 5) {
          setShouldFetchRecommendations(true);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [
    durationInSeconds,
    hasNextEpisode,
    shouldFetchRecommendations,
    subscribe
  ]);

  const setPlayingRecommendation: SetPlayingRecommendation = index =>
    (playingRecommendationIndexRef.current = index);

  useEffect(() => {
    if (
      programIdRef.current &&
      !isFullscreen &&
      previousIsFullScreen &&
      playingRecommendationIndexRef.current !== undefined
    ) {
      window.location.assign(
        getRecommendationUrl({
          autoplay: true,
          index: playingRecommendationIndexRef.current,
          language: languageRef.current,
          programId: programIdRef.current
        })
      );
    }
  }, [isFullscreen, previousIsFullScreen]);

  useEffect(() => {
    if (!isSeeking && previousIsSeeking) {
      setShowEndRecommendations(false);
    }
  }, [isSeeking, previousIsSeeking]);

  useEffect(() => {
    if (showEndRecommendations || showPauseRecommendations) {
      showRecommendationsOverlay?.();
    } else {
      hideRecommendationsOverlay?.();
    }
  }, [
    hideRecommendationsOverlay,
    showEndRecommendations,
    showPauseRecommendations,
    showRecommendationsOverlay
  ]);

  const previousProgramId = useRef(programId);

  useEffect(() => {
    if (previousProgramId.current !== programId) {
      setRecommendationsFetched(false);
    }
  }, [programId]);

  useEffect(() => {
    if (programId && shouldFetchRecommendations && !recommendationsFetched) {
      setRecommendationsFetched(true);
      fetchRecommendations(programId, contentType, language).then(response => {
        setCardResponse(response);
      });
    }
  }, [
    contentType,
    language,
    programId,
    recommendationsFetched,
    shouldFetchRecommendations
  ]);

  const recommendations = useMemo(
    () => cardResponse?.data || [],
    [cardResponse]
  );
  useEffect(() => {
    if (
      recommendationsFetched &&
      !recommendationsShown &&
      recommendations.length > 0 &&
      (showPauseRecommendations || showEndRecommendations)
    ) {
      setRecommendationsShown(true);
      recommendationsShownEvent(recommendations);
    }
  }, [
    recommendationsFetched,
    recommendationsShown,
    recommendations,
    showPauseRecommendations,
    showEndRecommendations
  ]);

  const onClose = () => {
    setShowPauseRecommendations(false);
    setBlockPauseRecommendations(true);
  };

  const commonCardProps: CommonCardProps = {
    language,
    newPlayerUi,
    setPlayingRecommendation,
    uiSize: getUiSize(uiSizeInPx),
    update
  };

  const analyticsContextYle = useMemo(
    () => cardResponse?.meta?.analytics?.context?.yle,
    [cardResponse]
  );

  const onViewRecommendation = useCallback(
    (recommendation: Card, index: number, viewUuid: string) => {
      const recommendationId = analyticsContextYle?.recommendation_id;
      const recommendationMethod = analyticsContextYle?.recommendation_method;

      const recommendationContext = {
        recommendation_id: recommendationId,
        method: recommendationMethod
      };

      const abTestName = analyticsContextYle?.ab_test_name;
      const abTestGroup = analyticsContextYle?.ab_test_group;

      const abTestContext = {
        name: abTestName,
        group: abTestGroup
      };

      getState().snowplowEvents?.onRecommendationView(
        recommendation,
        recommendations.length,
        index,
        viewUuid,
        recommendationContext,
        abTestContext
      );
    },
    [getState, recommendations.length, analyticsContextYle]
  );

  const onClickRecommendation = useCallback(
    (recommendation: Card, index: number, viewUuid: string) =>
      getState().snowplowEvents?.onRecommendationClick(
        recommendation,
        recommendations.length,
        index,
        viewUuid
      ),
    [getState, recommendations.length]
  );

  const onClickRecommendationPlay = useCallback(
    (recommendation: Card, index: number, viewUuid: string) =>
      getState().snowplowEvents?.onRecommendationPlayClick(
        recommendation,
        recommendations.length,
        index,
        viewUuid
      ),
    [getState, recommendations.length]
  );

  return (
    <>
      <EndRecommendations
        recommendations={recommendations}
        commonCardProps={commonCardProps}
        showRecommendations={!!recommendations.length && showEndRecommendations}
        onViewRecommendation={onViewRecommendation}
        onClickRecommendation={onClickRecommendation}
        onClickRecommendationPlay={onClickRecommendationPlay}
      />
      <PauseRecommendations
        commonCardProps={commonCardProps}
        disabled={disabled}
        onClose={onClose}
        recommendations={recommendations}
        showRecommendations={
          !!recommendations.length && showPauseRecommendations
        }
        onViewRecommendation={onViewRecommendation}
        onClickRecommendation={onClickRecommendation}
        onClickRecommendationPlay={onClickRecommendationPlay}
      />
    </>
  );
};

export default Recommendations;
